import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import useHttpClient from './useHttpClient';

export interface IDocUploadURL {
  contentType: string;
  documentId: { native: number; };
  url: string;
}

export interface IDocDataReq {
  projectId: { native: number; };
  folderId: { native: number; };
  filename: string;
  size: number;
}

export interface ICreateDocPayload {
  addDocument: (payload: IDocDataReq) => Promise<IDocUploadURL>;
  loading: boolean;
  error?: Error;
  called: boolean;
  docUploadURL?: IDocUploadURL;
}

const useCreateDoc = (): ICreateDocPayload => {
  const httpClient = useHttpClient();
  const [called, setCalled] = useState(false);

  const {
    mutate: addDocument,
    data: docUploadURL,
    error,
    isPending: loading,
  } = useMutation<IDocUploadURL, Error, IDocDataReq>({
    mutationFn: async (docData: IDocDataReq) => {
      const response = await httpClient.post(
        '/documents',
        docData,
        {
          timeout: 600000, // 10 minutes for large document uploads
        }
      );
      return response.data;
    },
  });

  const addDocumentWithPromise = (docData: IDocDataReq) => {
    setCalled(true);
    return new Promise<IDocUploadURL>((resolve, reject) => {
      addDocument(docData, {
        onSuccess: (data) => resolve(data),
        onError: (err) => reject(err),
      });
    });
  };

  return {
    addDocument: addDocumentWithPromise,
    error: error as Error | undefined,
    loading,
    called,
    docUploadURL: !error && docUploadURL ? docUploadURL : undefined,
  };
};

export default useCreateDoc;
